import { EOnboaringFormStep } from '@constants';
import { useEffect, useRef, useState } from 'react';

import { useSignWellScript } from '../../../../../hooks/useSignWellScript';

import { ContractWrapper, FormWrapper } from './Contract.styles';

import { useOnboardingFormLogic } from '@/hooks';
import { useUserOnboarding, useMutateUserContract, useProfile } from '@/store';

export const Contract: React.FC = () => {
  const { startLoadingAfterContract, currentStep } = useOnboardingFormLogic();
  const { isScriptLoaded, openSignWell, closeSignWell, resetSignWell } = useSignWellScript();
  const { generateUserContract, isWorking: isGenerating } = useMutateUserContract();
  const { profile } = useProfile();
  const { userOnboarding, refetch } = useUserOnboarding();
  const [isWaitingForNewContract, setIsWaitingForNewContract] = useState(true);
  const initialSignUrlRef = useRef<string | null>(null);
  const hasCalledGenerateRef = useRef(false);

  const handleSetIsContractSigned = () => {
    startLoadingAfterContract();
    closeSignWell();
  };

  useEffect(() => {
    initialSignUrlRef.current = userOnboarding?.contract?.signUrl || null;

    return () => {
      resetSignWell();
      setIsWaitingForNewContract(true);
      initialSignUrlRef.current = null;
      hasCalledGenerateRef.current = false;
    };
  }, []);

  useEffect(() => {
    refetch();
  }, [currentStep]);

  useEffect(() => {
    if (
      !isScriptLoaded ||
      !profile ||
      currentStep !== EOnboaringFormStep.SIGN_CONTRACT ||
      isGenerating ||
      !isWaitingForNewContract ||
      hasCalledGenerateRef.current
    ) {
      return;
    }

    if (userOnboarding?.contract?.isContractSigned) {
      setIsWaitingForNewContract(false);
      handleSetIsContractSigned();
      return;
    }

    hasCalledGenerateRef.current = true;
    generateUserContract();
  }, [
    isScriptLoaded,
    profile,
    currentStep,
    isGenerating,
    isWaitingForNewContract,
    userOnboarding?.contract?.isContractSigned
  ]);

  useEffect(() => {
    if (!isWaitingForNewContract || isGenerating) return;

    const currentSignUrl = userOnboarding?.contract?.signUrl;

    if (currentSignUrl && currentSignUrl !== initialSignUrlRef.current) {
      setIsWaitingForNewContract(false);
    }
  }, [userOnboarding?.contract?.signUrl, isGenerating, isWaitingForNewContract]);

  useEffect(() => {
    if (
      !isScriptLoaded ||
      currentStep !== EOnboaringFormStep.SIGN_CONTRACT ||
      !userOnboarding?.contract?.signUrl ||
      userOnboarding?.contract?.isContractSigned ||
      isWaitingForNewContract ||
      isGenerating
    ) {
      return;
    }

    openSignWell(userOnboarding.contract.signUrl, {
      onComplete: handleSetIsContractSigned,
      onClose: handleSetIsContractSigned
    });
  }, [
    isScriptLoaded,
    currentStep,
    userOnboarding?.contract?.signUrl,
    userOnboarding?.contract?.isContractSigned,
    isWaitingForNewContract,
    isGenerating
  ]);

  if (!profile || !profile.legalId || !profile.birthday) {
    return null;
  }

  return (
    <FormWrapper>
      <ContractWrapper id="contract-wrapper" />
    </FormWrapper>
  );
};
